import React from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { useOptionConfigurator } from '@/components/OptionConfigurator/OptionConfiguratorContext';
import { Room } from '@/components/OptionConfigurator/Room';
import { OptionConfiguratorTabs } from '@/components/OptionConfigurator/OptionConfiguratorTabs/OptionConfiguratorTabs';
import { MobileConfiguratorActions } from '@/components/OptionConfigurator/MobileConfiguratorActions';
import { MobileActionsProvider } from '@/components/OptionConfigurator/MobileActionsContext';
import { buildConfiguratorUrl } from '@/components/OptionConfigurator/utils/buildConfiguratorUrl';
import {
    OptionsPage as OptionsPageLegacy,
    OptionsScopeProvider as LegacyOptionsScopeProvider,
    OptionsFilterProvider,
} from '@/components/OptionConfigurator/legacyNext';

interface ShowroomConfiguratorProps {
    basePath: string;
}

// Chromeless configurator widget: top row is the room tab bar, the rest fills the remaining
// viewport height. No global Header, no page title, no bottom panel, no homepage. Designed to
// drop into a marketing site via iframe and let visitors browse options and make trial
// selections.
export const ShowroomConfigurator: React.FC<ShowroomConfiguratorProps> = ({ basePath }) => {
    const { configuratorData, projectData, baseUrl, selectedDeadlineId, residentialUnitId, rooms } =
        useOptionConfigurator();
    const isLoading = !configuratorData && !projectData;
    if (isLoading) return null;

    // Mirror the project's options view. The legacy configurator renders every deadline on one
    // page, so it just drops into a scroll container (no room tab bar / per-deadline routes). It
    // already reads data + mutations from the showroom contexts supplied above this component.
    if (projectData?.useLegacyOptionsView) {
        return (
            <div className="fit-height" style={{ width: '100%' }}>
                <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                    <LegacyOptionsScopeProvider
                        value={{
                            residentialUnitId,
                            projectData,
                            mode: 'buyer',
                            // The showroom is interactive (trial selections), but anonymous — so
                            // selections stay editable while the signing flow is hidden.
                            readOnly: false,
                            showSubmit: false,
                        }}>
                        <OptionsFilterProvider>
                            <OptionsPageLegacy />
                        </OptionsFilterProvider>
                    </LegacyOptionsScopeProvider>
                </div>
            </div>
        );
    }

    const firstRoomId = rooms[0]?.roomId;

    // Route paths include the `/deadline/:deadlineId` segment so they match the URLs produced by
    // `buildConfiguratorUrl` — which the shared tab bar / style cards use for every link. The
    // `:deadlineId` param itself is unused here (the showroom tracks the deadline in context,
    // not the URL); it only needs to be present for the route to match.
    return (
        <MobileActionsProvider>
            <div className="fit-height" style={{ width: '100%' }}>
                <OptionConfiguratorTabs mode="showroom" />
                {/* Scroll container for the routed page. The showroom disables body scroll, so the
                    content area must scroll itself. Room scrolls inside its own panel; styles are
                    picked in-room via the Styles section + modal. */}
                <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                    <Switch>
                        <Route exact path={`${basePath}/deadline/:deadlineId/room/:roomId`} component={Room} />
                        <Route exact path={basePath}>
                            {firstRoomId != null && selectedDeadlineId != null ? (
                                <Redirect
                                    to={buildConfiguratorUrl({
                                        baseUrl,
                                        deadlineId: selectedDeadlineId,
                                        segment: `room/${firstRoomId}`,
                                    })}
                                />
                            ) : null}
                        </Route>
                    </Switch>
                </div>
                <MobileConfiguratorActions mode="showroom" />
            </div>
        </MobileActionsProvider>
    );
};
